import { useEffect, useMemo, useState } from 'react';
import { getGameList } from '../services/game';
import { arrayToObj } from '../utils';
import { useCancelRequestHook } from './useCancelRequest';
import { usePageInfo } from './common'; 
import { getLevelUpGameList } from '@op/services';

const cancelObj: any = {};

export default function useGameList({
  edition_unified_id,
  entity_name,
  source_type,
  allList = false,
  otherParams = {},
  cancelKey,
}: any) {
  const [gameList, setGameList] = useState<Array<any>>([]);
  const [filterGames, setFilterGames] = useState<Array<any>>([]);
  const [allLoading, setAllLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const toServerGetGameInfo = (params?: {
    edition_unified_id: string;
    entity_name: string;
  }) => {
    if (cancelKey) {
      cancelObj[cancelKey]?.();
      cancelObj[cancelKey] = null;
    }

    return getGameList(
      {
        edition_unified_ids: params?.edition_unified_id
          ? [params?.edition_unified_id]
          : [],
        entity_name: params?.entity_name || '',
        need_mobile_editions: true,
        ...otherParams,
      },
      (cancelToken: any) => {
        if (cancelKey) {
          cancelObj[cancelKey] = cancelToken;
        }
      },
    ).then((data: any) => {
      if (params) {
        setFilterGames(data || []);
      } else {
        setGameList(data || []);
      }
    });
  };

  // 这里设置loading
  const getData = async (
    setLoadingFun: any,
    params?: { edition_unified_id: string; entity_name: string },
  ) => {
    setLoadingFun(true);
    // 先不要缓存
    try {
      await toServerGetGameInfo(params);
    } catch (error) {
    } finally {
      setLoadingFun(false);
    }
  };

  // 获取搜索列表
  useEffect(() => {
    if (edition_unified_id || entity_name) {
      getData(setLoading, { edition_unified_id, entity_name });
    }
  }, [edition_unified_id, entity_name]);

  // 获取所有列表
  useEffect(() => {
    if (allList) {
      getData(setAllLoading);
    }
  }, []);

  const filterGameList = useMemo(() => {
    let list = filterGames;
    // 如果啥都没有就使用所有的游戏列表
    if (!edition_unified_id && !entity_name) {
      list = gameList;
    }
    if (source_type !== 'all') {
      list = list.filter((item) => item.source_type === source_type);
    }
    return [...list];
  }, [filterGames, source_type, edition_unified_id, entity_name, gameList]);

  const allGameMap = useMemo(() => {
    return arrayToObj(gameList, 'edition_unified_id');
  }, [gameList]);

  return {
    allLoading,
    games: filterGameList,
    loading,
    allGameList: gameList,
    allGameMap,
  };
}

export function useLevelUpGameList({filter}: any) {
  const { loading, setLoading, wrapperRequestTile } = useCancelRequestHook();
  const { pageInfo, changePageInfo, changePageInfoNoRender } = usePageInfo({
    defaultPageInfo: { page: 1, page_size: 10 },
  });
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState<any>('');
  const [gameList, setGameList] = useState<Array<any>>([]);

  const toServerGetGameInfo = (params: any) => {
    return wrapperRequestTile(getLevelUpGameList, {
      entity_name: searchValue,
      need_mobile_editions: false,
      need_country_publisher: true,
      need_second_country_publisher: true,
      ...pageInfo,
      ...params,
      entity_types: filter
    }).then((data: any) => {
      let {list = [], total = 0} = data || {};
      list = list || [];
      setGameList((oldList:Array<any>) => {
        return [...oldList, ...list]
      }); 
      setTotal(total);
      return list.length > 0;
    });
  };

  // 获取搜索列表
  useEffect(() => {
    setGameList([]);
    toServerGetGameInfo({ page: 1, page_size: 10 });
  }, [searchValue]);

  return {
    loading,
    gameList,
    changePageInfo,
    searchValue,
    setSearchValue,
    nextPage: (params: any) => {
      changePageInfoNoRender(params);
    },
    pageInfo,
    setLoading,
    total,
    request: toServerGetGameInfo,
  };
}

export function mergeSearchGameList({
  gameList,
  excludeGameList,
  search,
}: {
  gameList: Array<any>;
  excludeGameList: Array<any>;
  search: string;
}) {
  if (
    !search?.trim() ||
    !(gameList?.length > 0) ||
    [
      '(',
      ')',
      '（',
      '）',
      ':',
      '：',
      '-',
      ';',
      '；',
      '.',
      '。',
      "'",
      '’',
      '"',
      '“',
      '”',
    ].includes(search?.trim())
  )
    return [];

  const excludeIdList = excludeGameList?.map?.(
    (item: any) => item.edition_unified_id,
  );

  return gameList.filter((item: any) => {
    const { edition_unified_id, mobile_edition_list = [] } = item;

    if (excludeIdList.includes(edition_unified_id)) return false; // 过滤相同的游戏

    const searchFields = ['entity_name', 'entity_name_cn', 'entity_name_en'];

    for (let index = 0; index < searchFields.length; index += 1) {
      const curSearchField = searchFields[index];

      if (item[curSearchField]?.toLowerCase()?.includes(search.toLowerCase())) {
        return true;
      }
    }

    for (
      let editionIndex = 0;
      editionIndex < mobile_edition_list?.length;
      editionIndex += 1
    ) {
      const item = mobile_edition_list[editionIndex];

      for (let index = 0; index < searchFields.length; index += 1) {
        const curSearchField = searchFields[index];

        if (
          item[curSearchField]?.toLowerCase()?.includes(search.toLowerCase())
        ) {
          return true;
        }
      }
    }

    return false;
  });
}
